import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Info,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
  X,
} from 'lucide-react';

import {
  useAssignUserRoleMutation,
  useCreateRoleTypeMutation,
  useDeleteRoleTypeMutation,
  useRevokeUserRoleMutation,
  useRoleTypesQuery,
  useRoleTypeRegistryQuery,
  useUpdateRoleTypeMutation,
  useUpdateUserRoleMutation,
  useUserRoleAssignmentsQuery,
} from '@/client/api/admin/roles';
import type {
  AdminUserSearchUser,
  UserAccountRole,
  UserAccountRoleType,
} from '@/client/types/admin/users-orgs';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { PERMISSIONS } from '@/client/config/permissions';
import { useListFilters } from '@/client/hooks/useListFilters';
import Pagination from '@/client/components/common/Pagination';
import SortableTh from '@/client/components/common/SortableTh';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { readStoredUser, writeStoredUser } from '@/client/utils/sessionUser';

const ROLE_USER_STORAGE_KEY = 'ff-admin:role-assignments:selected-user';

// POST /api/admin/user_role enforces role.create. user.role.assign does not.
const ROLES_PERMISSION = PERMISSIONS.role.create;

/** Default registry page size. Search, sort and pagination are all backend-
 *  driven via `GET /api/admin/role_type` (see `useRoleTypeRegistryQuery`). */
const PAGE_SIZE = 15;

// ─── Shared bits ──────────────────────────────────────────────────────────────

const ScopeNotice = () => (
  <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" aria-hidden="true" />
    <p>
      These are <strong>account roles</strong> (<code className="rounded bg-blue-100 px-1 py-0.5 text-xs">uartid</code>) —
      platform-wide. Organization roles (<code className="rounded bg-blue-100 px-1 py-0.5 text-xs">ourid</code>) are a{' '}
      <strong>global</strong> catalogue shared by every organization and are managed under Organizations → Roles.
    </p>
  </div>
);

// ─── Role-type create / edit dialog ───────────────────────────────────────────

interface RoleTypeDialogProps {
  open: boolean;
  /** `null` = create, otherwise edit that row. */
  roleType: UserAccountRoleType | null;
  onClose: () => void;
}

const RoleTypeDialog = ({ open, roleType, onClose }: RoleTypeDialogProps) => {
  const isEdit = roleType !== null;
  const [form, setForm] = useState({
    name: roleType?.name ?? '',
    description: roleType?.description ?? '',
    rank: roleType?.rank != null ? String(roleType.rank) : '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateRoleTypeMutation();
  const updateMutation = useUpdateRoleTypeMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const name = form.name.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }
    if (form.rank.trim() && Number.isNaN(Number(form.rank))) {
      setError('Rank must be a number.');
      return;
    }

    const payload = {
      name,
      description: form.description.trim() || undefined,
      rank: form.rank.trim() ? Number(form.rank) : undefined,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ uartid: roleType.uartid, ...payload });
        toast.success(`Role type “${name}” updated`);
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(`Role type “${name}” created`);
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save the role type.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            {isEdit ? 'Edit account role type' : 'Create account role type'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="rt-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="rt-name"
              placeholder="e.g. moderator"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rt-desc">Description</Label>
            <Input
              id="rt-desc"
              placeholder="What this role is for"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rt-rank">Rank</Label>
            <Input
              id="rt-rank"
              inputMode="numeric"
              placeholder="e.g. 10 — lower ranks outrank higher ones"
              value={form.rank}
              onChange={(e) => setForm((p) => ({ ...p, rank: e.target.value }))}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create role type'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Role-type registry ───────────────────────────────────────────────────────

interface RoleTypeRegistryProps {
  canManage: boolean;
}

const RoleTypeRegistry = ({ canManage }: RoleTypeRegistryProps) => {
  // Search, sort and page live in the URL (namespaced so they don't collide
  // with the assignment tab that shares this route). Filtering, sorting and
  // pagination are all backend-driven.
  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    setFilters,
    handlePageChange,
    handleLimitChange,
  } = useListFilters<{ sort_by: string; sort_order: 'asc' | 'desc' }>({
    initialFilters: { sort_by: 'uartid', sort_order: 'asc' },
    initialLimit: PAGE_SIZE,
    namespace: 'rt',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserAccountRoleType | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserAccountRoleType | null>(
    null
  );

  const { data, isLoading, isError } = useRoleTypeRegistryQuery({
    search: debouncedSearchTerm || undefined,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    page,
    page_size: limit,
  });

  const rows = data?.role_types ?? [];
  const total = data?.total ?? 0;

  const toggleSort = (field: string) => {
    const nextOrder =
      filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    setFilters({ sort_by: field, sort_order: nextOrder });
    handlePageChange(1);
  };

  const deleteMutation = useDeleteRoleTypeMutation();

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync({ uartid: pendingDelete.uartid });
      toast.success(`Role type “${pendingDelete.name}” deleted`);
      setPendingDelete(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete the role type.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search role types…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {canManage && (
          <div className="sm:ml-auto">
            <Button
              className="gap-2"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create role type
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh
                  label="ID"
                  field="uartid"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <SortableTh
                  label="Name"
                  field="name"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Description
                </th>
                <SortableTh
                  label="Rank"
                  field="rank"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-sm text-red-600"
                  >
                    Could not load role types.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    {debouncedSearchTerm
                      ? 'No role types match that search.'
                      : 'No account role types defined yet.'}
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                rows.map((rt) => (
                  <tr key={rt.uartid} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                        {rt.uartid}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {rt.name}
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-500">
                      {rt.description || '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {rt.rank ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(rt);
                              setDialogOpen(true);
                            }}
                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                            title="Edit role type"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(rt)}
                            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800"
                            title="Delete role type"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Read-only</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
            <Pagination
              total={total}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[15, 25, 50, 100]}
            />
          </div>
        )}
      </div>

      {dialogOpen && (
        <RoleTypeDialog
          // Remount per target so the form state initialises from the right row.
          key={editing?.uartid ?? 'create'}
          open={dialogOpen}
          roleType={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this account role type?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.name}” will be removed from the registry. Users
              currently holding this account role may lose the access it grants.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete role type'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─── Assign-roles dialog ──────────────────────────────────────────────────────

interface AssignRolesDialogProps {
  open: boolean;
  uid: number;
  assigned: UserAccountRole[];
  roleTypes: UserAccountRoleType[];
  onClose: () => void;
}

const AssignRolesDialog = ({
  open,
  uid,
  assigned,
  roleTypes,
  onClose,
}: AssignRolesDialogProps) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const assignMutation = useAssignUserRoleMutation();

  const assignedIds = useMemo(
    () => new Set(assigned.map((row) => row.uartid)),
    [assigned]
  );
  const available = roleTypes.filter((rt) => !assignedIds.has(rt.uartid));

  const toggle = (uartid: number) =>
    setSelected((prev) =>
      prev.includes(uartid)
        ? prev.filter((id) => id !== uartid)
        : [...prev, uartid]
    );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Re-filter at submit time: `POST /api/admin/user_role` raises a NameError
    // and 500s if every requested uartid is already assigned (gaps §6), and the
    // assignment list may have refetched since the checkboxes were rendered.
    const uartids = selected.filter((id) => !assignedIds.has(id));
    if (uartids.length === 0) {
      toast.error('Those account roles are already assigned to this user.');
      return;
    }

    try {
      await assignMutation.mutateAsync({
        uid,
        uartids,
        notes: notes.trim() || undefined,
      });
      // The response only carries the LAST role created (gaps §6), so we report
      // what we asked for and let the invalidated query supply the truth.
      toast.success(
        `Assigned ${uartids.length} account role${uartids.length === 1 ? '' : 's'}`
      );
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to assign the account roles.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-blue-600" />
            Assign account roles
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Account role types</Label>
            {available.length === 0 ? (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-500">
                This user already holds every account role type in the registry.
              </p>
            ) : (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2">
                {available.map((rt) => (
                  <label
                    key={rt.uartid}
                    className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selected.includes(rt.uartid)}
                      onChange={() => toggle(rt.uartid)}
                    />
                    <span>
                      <span className="font-medium text-gray-900">{rt.name}</span>
                      {rt.description && (
                        <span className="block text-xs text-gray-500">
                          {rt.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">
              Roles the user already holds are hidden — re-sending them makes the
              backend 500.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="assign-notes">Notes</Label>
            <textarea
              id="assign-notes"
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Why this user is being granted these roles (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={assignMutation.isPending || selected.length === 0}
            >
              {assignMutation.isPending ? 'Assigning…' : 'Assign roles'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Edit-notes dialog ────────────────────────────────────────────────────────

interface EditNotesDialogProps {
  open: boolean;
  assignment: UserAccountRole;
  onClose: () => void;
}

const EditNotesDialog = ({ open, assignment, onClose }: EditNotesDialogProps) => {
  const [notes, setNotes] = useState(assignment.notes ?? '');
  const updateMutation = useUpdateUserRoleMutation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await updateMutation.mutateAsync({
        uarid: assignment.uarid,
        notes: notes.trim() || null,
      });
      toast.success('Assignment notes updated');
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the notes.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit assignment notes</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <textarea
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save notes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── User assignment panel ────────────────────────────────────────────────────

interface UserAssignmentsProps {
  canManage: boolean;
}

const UserAssignments = ({ canManage }: UserAssignmentsProps) => {
  // Rehydrate the last-picked user so a reload keeps showing their name and
  // their loaded assignments instead of dropping back to a bare authid.
  const storedUser = readStoredUser(ROLE_USER_STORAGE_KEY);
  const [selectedUser, setSelectedUser] = useState<AdminUserSearchUser | null>(
    storedUser
  );
  const [userSearch, setUserSearch] = useState(
    storedUser ? `${storedUser.first_name} ${storedUser.last_name}`.trim() : ''
  );
  const [activeAuthid, setActiveAuthid] = useState<string | null>(
    storedUser?.authid ?? null
  );
  const [showAssign, setShowAssign] = useState(false);
  const [editingNotes, setEditingNotes] = useState<UserAccountRole | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<UserAccountRole | null>(
    null
  );

  const {
    data: assignments = [],
    isLoading,
    isFetching,
    isError,
  } = useUserRoleAssignmentsQuery(activeAuthid, Boolean(activeAuthid));
  const { data: roleTypes = [] } = useRoleTypesQuery();
  const revokeMutation = useRevokeUserRoleMutation();

  const roleTypeById = useMemo(
    () => new Map(roleTypes.map((rt) => [rt.uartid, rt])),
    [roleTypes]
  );

  const resolvedUid = assignments[0]?.uid ?? selectedUser?.uid ?? null;
  const uidIsValid = resolvedUid !== null && Number.isFinite(resolvedUid) && resolvedUid > 0;

  const selectedName = selectedUser
    ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
    : null;

  const handleRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revokeMutation.mutateAsync({ uarid: pendingRevoke.uarid });
      toast.success('Account role revoked');
      setPendingRevoke(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to revoke the account role.');
    }
  };

  const busy = isLoading || isFetching;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-5 shadow">
        <h3 className="mb-1 text-sm font-semibold text-gray-700">
          Look up a user&rsquo;s account roles
        </h3>
        <p className="mb-3 text-xs text-gray-500">
          Search for a user by name or email and pick them to load their
          platform-wide account roles.
        </p>
        <UserSearchSelect
          className="max-w-md"
          value={userSearch}
          onChange={setUserSearch}
          onSelect={(user) => {
            setSelectedUser(user);
            setUserSearch(`${user.first_name} ${user.last_name}`.trim());
            setActiveAuthid(user.authid);
            writeStoredUser(ROLE_USER_STORAGE_KEY, user);
          }}
        />
      </div>

      {activeAuthid && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Account roles for{' '}
                <span className="text-blue-700">
                  {selectedName ?? activeAuthid}
                </span>
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {assignments.length} assignment
                {assignments.length === 1 ? '' : 's'}
                {uidIsValid ? ` · uid ${resolvedUid}` : ''}
              </p>
            </div>
            {canManage && (
              <Button
                size="sm"
                className="gap-2"
                disabled={!uidIsValid}
                title={
                  uidIsValid
                    ? undefined
                    : 'A numeric uid is required to create an assignment'
                }
                onClick={() => setShowAssign(true)}
              >
                <Plus className="h-4 w-4" />
                Assign account role
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Role', 'Assignment ID', 'Active', 'Notes', 'Assigned', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                          h === 'Actions' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {busy &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!busy && isError && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-red-600"
                    >
                      Could not load account roles for that authid.
                    </td>
                  </tr>
                )}

                {!busy && !isError && assignments.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-gray-500"
                    >
                      This user holds no account roles.
                    </td>
                  </tr>
                )}

                {!busy &&
                  !isError &&
                  assignments.map((row) => {
                    const roleType = roleTypeById.get(row.uartid);
                    return (
                      <tr key={row.uarid} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                          {roleType?.name ?? `uartid ${row.uartid}`}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                            {row.uarid}
                          </code>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              row.active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {row.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-500">
                          {row.notes || '—'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {row.date_added
                            ? new Date(row.date_added).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right">
                          {canManage ? (
                            <div className="flex items-center justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => setEditingNotes(row)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                                title="Edit notes"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingRevoke(row)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800"
                                title="Revoke account role"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Read-only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAssign && uidIsValid && (
        <AssignRolesDialog
          open={showAssign}
          uid={resolvedUid as number}
          assigned={assignments}
          roleTypes={roleTypes}
          onClose={() => setShowAssign(false)}
        />
      )}

      {editingNotes && (
        <EditNotesDialog
          key={editingNotes.uarid}
          open
          assignment={editingNotes}
          onClose={() => setEditingNotes(null)}
        />
      )}

      <AlertDialog
        open={pendingRevoke !== null}
        onOpenChange={(next) => !next && setPendingRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this account role?</AlertDialogTitle>
            <AlertDialogDescription>
              The user will immediately lose the platform-wide access granted by{' '}
              “{roleTypeById.get(pendingRevoke?.uartid ?? -1)?.name ??
                `uartid ${pendingRevoke?.uartid}`}”. Their organization roles are
              unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking…' : 'Revoke role'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'registry' | 'assignments';

const RolesPage = () => {
  useBreadcrumbs([
    { label: 'Users Management', href: '#' },
    { label: 'Account Roles' },
  ]);

  const [activeTab, setActiveTab] = useState<Tab>('registry');
  const { hasPermission, isSuperAdmin } = useHasPermission(ROLES_PERMISSION);
  const canManage = hasPermission || isSuperAdmin;

  const TabButton = ({
    id,
    label,
    icon: Icon,
  }: {
    id: Tab;
    label: string;
    icon: typeof ShieldCheck;
  }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
        activeTab === id
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6">
          <div className="mb-1 flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Account roles</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Manage the platform-wide account role registry and who holds each role
          </p>
        </div>

        <div className="mb-4">
          <ScopeNotice />
        </div>

        {!canManage && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            You do not have the{' '}
            <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">
              {ROLES_PERMISSION}
            </code>{' '}
            permission, so this page is read-only. Ask an administrator to grant
            it if you need to change roles.
          </div>
        )}

        <div className="rounded-t-lg border border-b-0 bg-white shadow">
          <div className="flex border-b px-2">
            <TabButton id="registry" label="Role type registry" icon={ShieldCheck} />
            <TabButton id="assignments" label="User assignments" icon={UserCog} />
          </div>
        </div>

        <div className="rounded-b-lg bg-gray-50 p-1">
          <div className="pt-4">
            {activeTab === 'registry' && (
              <RoleTypeRegistry canManage={canManage} />
            )}
            {activeTab === 'assignments' && (
              <UserAssignments canManage={canManage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
